import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

import lruCache from '../assets/lru.png';
import bufferPoolImg from '../assets/bplustree.png';
import udpFileTransfer from '../assets/udp.jpeg';
import httpProxy from '../assets/httpproxy.jpeg';
import dfs from '../assets/dfs.png';
import redis from '../assets/redis.webp';
import webserver from '../assets/webserver.png';
import spark from '../assets/spark.png';
import biocollate from '../assets/biocollate.png';

import urlShortenerImg from '../assets/url-shortener.png';
import defenseGame from '../assets/defense-game.png';
import turingChatbot from '../assets/chatbot.png';
import cyberAttackRL from '../assets/cybersecurity-project.png';
import fakeNewsDetection from '../assets/fake-news-project.png';
import healthcareApp from '../assets/healthcare.png';
import assessmentPortal from '../assets/assessment.png';
import aistudy from '../assets/aistudy.jpeg';
import malware from '../assets/malware.jpeg';

/* ── SECTION 1: Systems & Infrastructure ── */
const systemsProjects = [
  {
    id: 's1',
    title: 'Distributed KV Cache for LLM Inference',
    metric: '80–85% hit rate (LFU/Learned) · 35% TTFT reduction',
    description:
      'Tiered GPU/CPU/disk KV cache for LLaMA-3.1-8B inference, benchmarking four eviction policies at scale. LFU and an online logistic-regression Learned policy sustained 80–85% hit rates vs. 72–75% for LRU. Discovered that GPU+CPU+Disk tiering underperformed GPU+CPU alone — PD disaggregation transfer overhead dominated and erased the disk tier\'s capacity benefit.',
    arch: 'Metadata registry with per-entry heartbeat/TTL tracks cache liveness across tiers. Eviction policy is hot-swappable at runtime. Benchmarking harness uses Slurm job arrays on H100 nodes to isolate policy overhead from IO jitter. Learned policy trains an online logistic classifier on recency/frequency/size features with no offline phase.',
    tags: ['Python', 'PyTorch', 'HuggingFace Transformers', 'Slurm', 'NVIDIA H100'],
    github: null,
    featured: true,
    gradient: 'linear-gradient(135deg, #0a1628 0%, #001a0a 50%, #0d0d1a 100%)',
    accentColor: '#00ff41',
  },
  {
    id: 's2',
    title: 'Adaptive Layer 7 Load Balancer',
    metric: 'EMA latency tracking · 3 routing algorithms · active health checks',
    description:
      'HTTP reverse proxy in Go with round-robin, least-connections, and weighted routing. Active health checking removes failed upstreams from rotation within one interval. EMA-based latency tracking smooths response-time spikes so routing decisions reflect sustained load, not momentary bursts.',
    arch: 'Hand-rolled Prometheus metrics endpoint — no client library — exposes per-upstream request count, error rate, and p95 latency. TLS termination handled at the proxy; backends run plaintext. Config is hot-reloadable YAML; upstream pool changes apply without restart.',
    tags: ['Go', 'Prometheus', 'TLS', 'YAML', 'Reverse Proxy'],
    github: null,
    featured: true,
    gradient: 'linear-gradient(135deg, #0a1a0a 0%, #001228 50%, #0a1a0a 100%)',
    accentColor: '#00ffff',
  },
  {
    id: 's3',
    title: 'Distributed File System',
    metric: '3-way replication · chunk-level fault tolerance',
    description:
      'TCP-based DFS in C. Files are split into fixed-size chunks distributed across storage nodes with a replication factor of 3. Reads hit any replica; writes go to the primary which propagates to backups before acknowledging the client — strong consistency at the cost of write latency. Node failure is detected via heartbeat timeouts; surviving replicas serve reads without coordinator intervention.',
    arch: 'Namenode tracks chunk-to-node mapping in memory; no persistent log in this implementation. Chunking size is tunable to trade seek overhead against metadata overhead. Fault recovery is manual rebalance — automatic re-replication was the designed but unimplemented next step.',
    tags: ['C', 'TCP', 'Replication', 'Fault Tolerance', 'Distributed Systems'],
    github: 'https://github.com/DivitaP/Distributed-File-System',
    image: dfs,
  },
  {
    id: 's4',
    title: 'HTTP Proxy Cache Server',
    metric: 'Background prefetch on all HTML links · POSIX regex blocklist',
    description:
      'Multithreaded HTTP proxy in C. Parses HTML responses and prefetches every linked resource in background pthreads before the browser requests them — subsequent navigations hit the in-memory cache cold. Keep-alive connections eliminate per-request TCP handshake overhead on repeat visits to the same origin.',
    arch: 'Prefetch threads share a single hash-map cache protected by a reader-writer lock — multiple readers, exclusive writer — to avoid serializing cache reads during prefetch storms. Site blocking uses POSIX regcomp/regexec against the Host header so patterns compose naturally.',
    tags: ['C', 'Pthreads', 'HTTP', 'Caching', 'Sockets', 'POSIX Regex'],
    github: 'https://github.com/DivitaP/HTTP-Proxy-Cache-Server',
    image: httpProxy,
  },
  {
    id: 's5',
    title: 'Multithreaded HTTP Web Server',
    metric: 'zero-copy sendfile · thread-pool, no per-request threads',
    description:
      'HTTP/1.1 server in C using a fixed thread pool. File serving uses sendfile(2) to transfer directly from page cache to socket — eliminates the kernel→user→kernel copy that read+write incurs. Pool size is set at startup; the request queue is a bounded buffer with producer-consumer synchronization via semaphores.',
    arch: 'Thread-per-request was benchmarked first; at high concurrency the thread-creation overhead dominated. Switching to a pre-allocated pool dropped latency under load by ~40% in informal benchmarks. sendfile path is taken only for static files; dynamic responses fall back to write.',
    tags: ['C', 'HTTP/1.1', 'sendfile', 'Thread Pool', 'Sockets'],
    github: 'https://github.com/DivitaP/Multithreaded-Web-Server',
    image: webserver,
  },
  {
    id: 's6',
    title: 'B+ Tree Index & Buffer Pool Manager',
    metric: 'O(log n) range scans · latch-coupling for concurrent access',
    description:
      'Database storage engine component in C++. Buffer pool uses LRU replacement with dirty-page tracking to defer disk writes until eviction. B+ Tree supports concurrent access via crabbing (latch coupling): shared latches on reads, exclusive on splits/merges, so readers never block writers on non-conflicting pages.',
    arch: 'Latch granularity tradeoff was deliberate — page-level latches over row-level to keep the implementation tractable. Range-scan iterators hold a shared latch on the leaf page and release-and-reacquire as they advance, tolerating concurrent inserts without full tree locks.',
    tags: ['C++', 'B+ Tree', 'Concurrency', 'Buffer Pool', 'Database Internals'],
    image: bufferPoolImg,
  },
  {
    id: 's7',
    title: 'UDP File Transfer System',
    metric: '16KB chunks · stop-and-wait ARQ · configurable retransmit timeout',
    description:
      'Reliable file transfer over raw UDP in C implementing stop-and-wait ARQ. Each 16KB chunk carries a sequence number; the receiver sends a cumulative ACK; the sender retransmits on timeout (setsockopt SO_RCVTIMEO). Designed under a hard no-TCP constraint, which made reliability entirely the application\'s problem.',
    arch: 'Stop-and-wait was chosen over sliding window to keep sender state minimal — one outstanding chunk, one timer, one retransmit queue entry. The tradeoff is throughput: on any link with >1ms RTT, the pipe is nearly empty. Sliding window was the planned extension.',
    tags: ['C', 'UDP', 'ARQ', 'Networking', 'File I/O'],
    github: 'https://github.com/DivitaP/UDP-File-Transfer-System',
    image: udpFileTransfer,
  },
  {
    id: 's8',
    title: 'Redis Caching Layer',
    metric: 'Cache-aside + write-through · per-class TTL strategy',
    description:
      'Caching middleware implementing two complementary patterns. Cache-aside: application checks Redis before DB and writes back on miss, keeping the DB authoritative. Write-through: every DB mutation propagates to Redis synchronously, so the cache is never stale at the cost of write latency. TTL strategies differ by data class — short TTL for session state, longer TTL with explicit invalidation for reference data.',
    arch: 'The key design question was consistency vs. availability during Redis downtime. The implementation degrades gracefully: cache misses fall through to the DB, so Redis failure degrades performance but not correctness. A circuit breaker could short-circuit the Redis call after N consecutive failures.',
    tags: ['Redis', 'Python', 'Cache-Aside', 'Write-Through', 'TTL'],
    image: redis,
  },
  {
    id: 's9',
    title: 'Thread-Safe LRU Cache',
    metric: 'O(1) get/put/evict · concurrent bulk warming · TTL expiry',
    description:
      'Production-grade LRU cache in Java 17 with O(1) get/put/evict using ConcurrentHashMap and a hand-rolled doubly-linked list. TTL expiry and AtomicLong statistics per operation. CacheLoader handles automatic miss resolution; concurrent bulk warming pre-populates the cache from a data source without holding a global lock.',
    arch: 'The linked list is guarded by a ReentrantReadWriteLock rather than synchronized — reads take a shared lock, structural mutations take exclusive. This avoids serializing reads through the LRU promotion path at the cost of two lock acquisitions per read-hit. Bulk warming submits individual loads to a ForkJoinPool so warming latency scales with parallelism.',
    tags: ['Java 17', 'ConcurrentHashMap', 'ReentrantReadWriteLock', 'AtomicLong', 'JUnit 5'],
    github: 'https://github.com/DivitaP/thread-safe-lru-cache',
    demo: 'https://medium.com/@divitaphadakale21/building-a-thread-safe-lru-cache-from-scratch-in-java-17-2f05abdfb237',
    image: lruCache,
    featured: true,
  },
  {
    id: 's10',
    title: 'Apache Spark Processing Pipeline',
    metric: 'Distributed RDD + DataFrame analytics at scale',
    description:
      'Distributed analytics pipeline using Spark RDDs and DataFrames for large-scale data processing. RDD-level transformations used where schema flexibility was needed; DataFrame API used for query-optimized aggregations — letting Catalyst rewrite plans that would be expensive as manual RDD ops.',
    arch: 'Partition count was tuned to match available cores; over-partitioning incurred scheduling overhead that outweighed parallelism gains. PySpark UDFs were minimized — they serialize rows to Python and back per row, which can make a DataFrame job slower than the equivalent RDD map.',
    tags: ['PySpark', 'Spark RDD', 'DataFrame', 'Distributed Computing'],
    image: spark,
  },
];

/* ── SECTION 2: AI Infrastructure & Agentic Systems ── */
const agentProjects = [
  {
    id: 'a1',
    title: 'Adaptive API Shield',
    metric: 'Kafka-streamed anomaly → LLM incident report in <2s',
    description:
      'Agentic API security gateway with adaptive rate limiting and real-time anomaly detection. Spring Boot gateway publishes all request events to Kafka. A FastAPI consumer runs adaptive rate limiting (leaky bucket with per-client anomaly scoring); anomalous bursts trigger a Groq-hosted LLM to generate an incident analysis report. Deployed on Oracle Cloud with Confluent Cloud Kafka.',
    arch: 'The Kafka decoupling means the gateway never blocks on anomaly scoring — it publishes and moves on. The FastAPI consumer can scale independently. Redis stores per-client counters with TTL so rate-limit state doesn\'t require a DB round-trip on the hot path.',
    tags: ['Spring Boot', 'Kafka', 'FastAPI', 'Redis', 'Groq LLM', 'Oracle Cloud'],
    github: null,
    featured: true,
    gradient: 'linear-gradient(135deg, #1a0a28 0%, #0a1a28 100%)',
    accentColor: '#b794f6',
  },
  {
    id: 'a2',
    title: 'Cold Case',
    metric: 'Two-stage contradiction detection · 3rd place HackCU',
    description:
      'Multi-agent murder mystery app with two-stage contradiction detection. Stage 1: FAISS cosine similarity over testimony vectors identifies candidate contradictions cheaply. Stage 2: Gemini 2.5 Flash LLM judge confirms or rejects — only called when cosine distance exceeds a threshold, so the expensive LLM call is gated. Suspect interrogation is knowledge-graph-gated via Neo4j AuraDB to prevent hallucinated answers.',
    arch: 'The cosine gate was the key perf decision: running Gemini on every testimony pair would be cost-prohibitive and slow. The graph gate on interrogation was a correctness decision — unconstrained LLM answers would break the mystery. AuraDB stores the ground-truth case facts; the agent can only answer questions the graph can support.',
    tags: ['FastAPI', 'FAISS', 'Neo4j AuraDB', 'Gemini 2.5 Flash', 'React', 'Vite'],
    github: null,
    featured: true,
    gradient: 'linear-gradient(135deg, #1a0a0a 0%, #0a0a1a 100%)',
    accentColor: '#ff6b35',
  },
  {
    id: 'a3',
    title: 'BioCollate — Benchtop Lab Assistant',
    metric: 'Local-first · E2E encrypted · tamper-evident BFT audit log',
    description:
      'Secure peer-to-peer collaboration platform for synthetic biology researchers. WebRTC data channels handle E2E encrypted real-time sync — the relay server never sees plaintext. An audit layer built on CometBFT (Tendermint-derived BFT consensus) produces a tamper-evident append-only log; any mutation is detectable by any participant.',
    arch: 'CometBFT was chosen over a simple hash-chain because it provides Byzantine fault tolerance — a single compromised node can\'t silently rewrite history. The tradeoff is consensus overhead: every audit entry requires a round of BFT voting, which is acceptable for lab audit events (low frequency) but would be a bottleneck for real-time cursor sync (handled by WebRTC instead).',
    tags: ['WebRTC', 'Go', 'CometBFT', 'Flutter', 'Dart', 'E2E Encryption'],
    github: null,
    featured: true,
    image: biocollate,
  },
  {
    id: 'a4',
    title: 'DFIR Autonomous Investigation Agent',
    metric: 'Dual-agent memory forensics · SANS Find Evil! submission',
    description:
      'Dual-agent pipeline for automated incident investigation from memory dumps. Agent 1 runs Volatility 3 plugins against the dump and emits structured findings. Agent 2 uses a Groq-hosted LLM to correlate findings across plugins, classify threat actors by TTP pattern, and generate an investigator-ready report.',
    arch: 'The two-agent split was deliberate: Volatility output is structured but verbose; feeding raw plugin output directly to an LLM wastes context window on noise. Agent 1 normalizes and filters; Agent 2 reasons over the condensed finding set. Groq was chosen for inference speed — latency matters when triage is time-sensitive.',
    tags: ['Volatility 3', 'Python', 'Groq LLM', 'Memory Forensics', 'DFIR'],
    github: null,
    gradient: 'linear-gradient(135deg, #0a0f1a 0%, #1a0a0f 100%)',
    accentColor: '#00ffff',
  },
];

/* ── SECTION 3: Other Projects ── */
const otherProjects = [
  {
    id: 'o1',
    title: 'URL Shortener API',
    description: 'Production-grade REST API with Spring Boot 3 and Java 17. Secure 6-char code generation, click tracking, expiry management, scheduled cleanup, and Spring Actuator observability.',
    tags: ['Java 17', 'Spring Boot 3', 'PostgreSQL', 'Docker', 'JUnit 5'],
    github: 'https://github.com/DivitaP/url-shortener',
    demo: 'https://drive.google.com/file/d/1_dTrJ5SrbOhC8JrMzls2P1NKisAwmqlZ/view?usp=drive_link',
    image: urlShortenerImg,
  },
  {
    id: 'o2',
    title: 'Online Assessment Portal',
    description: 'Full-stack quiz platform with Spring Boot, Angular, and MongoDB. OTP-based student registration via email, CRUD for students and quizzes, Spring Mail notification service.',
    tags: ['Spring Boot', 'Angular', 'MongoDB', 'Java', 'REST API'],
    github: 'https://github.com/DivitaP/online-assessment-portal',
    image: assessmentPortal,
  },
  {
    id: 'o3',
    title: 'Healthcare Data Management',
    description: 'MERN stack platform for managing patient records, doctor profiles, and appointments. JWT authentication, role-based access control, RESTful Express API with Mongoose schemas.',
    tags: ['MongoDB', 'Express.js', 'React', 'Node.js', 'JWT'],
    github: 'https://github.com/DivitaP/BDA-HealthCare-MERN',
    image: healthcareApp,
  },
  {
    id: 'o4',
    title: 'Tower Defense: Design Patterns Showcase',
    description: 'Tower defense game demonstrating 13 design patterns. 3 tower types, 4 enemy types, real-time combat, 60 FPS gameplay with clean OOP architecture in Java Swing.',
    tags: ['Java 11+', 'Swing', 'Design Patterns', 'OOP', 'SOLID'],
    github: 'https://github.com/DivitaP/java-tower-defense-game',
    image: defenseGame,
  },
  {
    id: 'o5',
    title: 'Temporal Chatbot – Alan Turing',
    description: 'Historically accurate chatbot using Neo4j knowledge graphs and GPT API, restricting responses to Turing\'s lifetime (1912–1954). 93% historical correctness via graph-gated retrieval.',
    tags: ['Python', 'Neo4j', 'OpenAI API', 'Flask', 'LangChain'],
    github: 'https://github.com/DivitaP/temporal-minds',
    image: turingChatbot,
  },
  {
    id: 'o6',
    title: 'Cyber-Attack Detection via RL',
    description: 'Real-time DDoS detection using Q-learning to identify anomalous network traffic. Trained and tested in simulated attack environments using Kali Linux and Wireshark.',
    tags: ['Python', 'TensorFlow', 'Q-Learning', 'Wireshark', 'Kali Linux'],
    github: 'https://github.com/DivitaP/Cyber-Attack-Detection',
    demo: 'https://youtu.be/HnYItTA1ay8?si=l4RNhoXJDJY8sVI8',
    image: cyberAttackRL,
  },
  {
    id: 'o7',
    title: 'Fake News Detection',
    description: 'Binary classification comparing LR, SVM, Perceptron, and Multinomial Naïve Bayes. 89% accuracy with LR using TF-IDF vectorization, deployed as a CLI tool.',
    tags: ['Python', 'Scikit-learn', 'NLTK', 'TF-IDF', 'NLP'],
    github: 'https://github.com/DivitaP/fake-news-detection',
    image: fakeNewsDetection,
  },
  {
    id: 'o8',
    title: 'AI Study Assistant',
    description: 'Transformer-based summarization and quiz generation assistant. Flask backend with HuggingFace NLP models; built at a hackathon.',
    tags: ['Python', 'HuggingFace', 'Flask', 'NLP'],
    github: 'https://github.com/divitas/hackcu/tree/main',
    image: aistudy,
  },
  {
    id: 'o9',
    title: 'Malware Analysis — njRAT',
    description: 'Static and dynamic analysis of njRAT sample using ProcMon and Wireshark. Documents C2 traffic patterns, persistence mechanisms, and IOCs.',
    tags: ['Malware Analysis', 'Wireshark', 'ProcMon', 'Reverse Engineering'],
    image: malware,
  },
];

/* ── Card components ── */

function ProjectVisual({ project }) {
  if (project.image) {
    return (
      <div className="proj-img-wrap">
        <img src={project.image} alt={project.title} className="proj-img" />
      </div>
    );
  }
  return (
    <div
      className="proj-gradient-wrap"
      style={{ background: project.gradient || 'linear-gradient(135deg, #0a1628, #0d0d1a)' }}
    >
      <span className="proj-gradient-title" style={{ color: project.accentColor || '#00ff41' }}>
        {project.title.split(' ').slice(0, 3).join(' ')}
      </span>
    </div>
  );
}

function SystemsCard({ project }) {
  return (
    <motion.div
      className={`sys-card ${project.featured ? 'sys-card--featured' : ''}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4 }}
      whileHover={{ y: -6 }}
    >
      <ProjectVisual project={project} />

      <div className="sys-card-body">
        {project.metric && (
          <div className="metric-chip">
            <span className="metric-dot" />
            <code>{project.metric}</code>
          </div>
        )}

        <h3 className="sys-card-title">{project.title}</h3>
        <p className="sys-card-desc">{project.description}</p>

        {project.arch && (
          <div className="arch-panel">
            <div className="arch-label">Architecture notes</div>
            <p className="arch-text">{project.arch}</p>
          </div>
        )}

        <div className="tag-row">
          {project.tags.map(t => <code key={t} className="stack-tag">{t}</code>)}
        </div>

        <div className="card-links">
          {project.github && project.github !== '#' && (
            <a href={project.github} target="_blank" rel="noopener noreferrer" className="card-link">
              GitHub →
            </a>
          )}
          {project.demo && project.demo !== '#' && (
            <a href={project.demo} target="_blank" rel="noopener noreferrer" className="card-link card-link--secondary">
              Demo →
            </a>
          )}
          {!project.github && (
            <span className="card-link card-link--muted">Private / link TBD</span>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function AgentCard({ project }) {
  return (
    <motion.div
      className="agent-card"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4 }}
      whileHover={{ y: -6 }}
    >
      <ProjectVisual project={project} />

      <div className="sys-card-body">
        {project.metric && (
          <div className="metric-chip metric-chip--purple">
            <span className="metric-dot metric-dot--purple" />
            <code>{project.metric}</code>
          </div>
        )}

        <h3 className="sys-card-title">{project.title}</h3>
        <p className="sys-card-desc">{project.description}</p>

        {project.arch && (
          <div className="arch-panel">
            <div className="arch-label">Architecture notes</div>
            <p className="arch-text">{project.arch}</p>
          </div>
        )}

        <div className="tag-row">
          {project.tags.map(t => <code key={t} className="stack-tag">{t}</code>)}
        </div>

        <div className="card-links">
          {project.github && project.github !== '#' ? (
            <a href={project.github} target="_blank" rel="noopener noreferrer" className="card-link">
              GitHub →
            </a>
          ) : (
            <span className="card-link card-link--muted">Private / link TBD</span>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function OtherCard({ project }) {
  return (
    <motion.div
      className="other-card"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{ duration: 0.3 }}
    >
      {project.image && (
        <div className="other-img-wrap">
          <img src={project.image} alt={project.title} />
        </div>
      )}
      <div className="other-body">
        <h4 className="other-title">{project.title}</h4>
        <p className="other-desc">{project.description}</p>
        <div className="tag-row tag-row--sm">
          {project.tags.map(t => <code key={t} className="stack-tag stack-tag--sm">{t}</code>)}
        </div>
        <div className="card-links">
          {project.github && project.github !== '#' && (
            <a href={project.github} target="_blank" rel="noopener noreferrer" className="card-link card-link--sm">
              GitHub →
            </a>
          )}
          {project.demo && project.demo !== '#' && (
            <a href={project.demo} target="_blank" rel="noopener noreferrer" className="card-link card-link--sm card-link--secondary">
              Demo →
            </a>
          )}
        </div>
      </div>
    </motion.div>
  );
}

/* ── Section header ── */
function SectionLabel({ tag, title, subtitle }) {
  return (
    <div className="proj-section-header">
      <span className="section-tag">
        <span className="tag-bracket">{'<'}</span>
        {tag}
        <span className="tag-bracket">{'/>'}</span>
      </span>
      <h2 className="proj-section-title">{title}</h2>
      {subtitle && <p className="proj-section-sub">{subtitle}</p>}
    </div>
  );
}

/* ── Main component ── */
const Projects = () => {
  const [ref] = useInView({ triggerOnce: true, threshold: 0.05 });
  const [showOther, setShowOther] = useState(false);

  return (
    <section className="projects" id="projects" ref={ref}>
      <div className="container">

        {/* ── Systems & Infrastructure ── */}
        <SectionLabel
          tag="systems"
          title="Systems & Infrastructure"
          subtitle="Backend systems, distributed infrastructure, and performance-critical implementations."
        />

        <div className="sys-grid">
          {systemsProjects.map(p => <SystemsCard key={p.id} project={p} />)}
        </div>

        {/* ── AI Infrastructure & Agentic Systems ── */}
        <div className="proj-divider" />

        <SectionLabel
          tag="agentic"
          title="AI Infrastructure & Agentic Systems"
          subtitle="Backend meets AI — event-driven pipelines, multi-agent orchestration, and production-grade AI infrastructure."
        />

        <div className="sys-grid">
          {agentProjects.map(p => <AgentCard key={p.id} project={p} />)}
        </div>

        {/* ── Other Projects ── */}
        <div className="proj-divider" />

        <div className="other-toggle-row">
          <button
            className="other-toggle-btn"
            onClick={() => setShowOther(v => !v)}
          >
            {showOther ? '− Hide' : '+ Show'} other projects ({otherProjects.length})
          </button>
        </div>

        <AnimatePresence>
          {showOther && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.4 }}
              style={{ overflow: 'hidden' }}
            >
              <div className="other-grid">
                {otherProjects.map(p => <OtherCard key={p.id} project={p} />)}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

      </div>

      <style>{`
        /* ── Layout ── */
        .projects { padding: 80px 0 100px; }

        .proj-section-header {
          margin-bottom: 48px;
          text-align: left;
        }

        .proj-section-title {
          font-size: 2.2rem;
          font-weight: 800;
          margin: 12px 0 8px;
          background: linear-gradient(135deg, #fff, #aaa);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .proj-section-sub {
          font-size: 0.95rem;
          color: var(--text-secondary);
          max-width: 600px;
        }

        .proj-divider {
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent);
          margin: 72px 0;
        }

        /* ── Systems grid ── */
        .sys-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
          gap: 24px;
        }

        /* ── Systems card ── */
        .sys-card {
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(255,255,255,0.07);
          border-radius: 14px;
          overflow: hidden;
          transition: border-color 0.3s, box-shadow 0.3s;
          display: flex;
          flex-direction: column;
        }

        .sys-card:hover {
          border-color: var(--accent-green);
          box-shadow: 0 0 32px rgba(0,255,65,0.1);
        }

        .sys-card--featured {
          border-color: rgba(0,255,65,0.25);
        }

        .agent-card {
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(183,148,246,0.15);
          border-radius: 14px;
          overflow: hidden;
          transition: border-color 0.3s, box-shadow 0.3s;
          display: flex;
          flex-direction: column;
        }

        .agent-card:hover {
          border-color: var(--accent-purple);
          box-shadow: 0 0 32px rgba(183,148,246,0.12);
        }

        /* ── Project visuals ── */
        .proj-img-wrap {
          height: 180px;
          overflow: hidden;
        }

        .proj-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.4s ease;
        }

        .sys-card:hover .proj-img,
        .agent-card:hover .proj-img {
          transform: scale(1.04);
        }

        .proj-gradient-wrap {
          height: 140px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }

        .proj-gradient-title {
          font-family: 'Courier New', monospace;
          font-size: 1rem;
          font-weight: 700;
          text-align: center;
          line-height: 1.5;
          opacity: 0.85;
        }

        /* ── Card body ── */
        .sys-card-body {
          padding: 20px 22px 22px;
          display: flex;
          flex-direction: column;
          gap: 12px;
          flex: 1;
        }

        /* ── Metric chip ── */
        .metric-chip {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          padding: 5px 10px;
          background: rgba(0,255,65,0.06);
          border: 1px solid rgba(0,255,65,0.2);
          border-radius: 6px;
          width: fit-content;
        }

        .metric-chip code {
          font-family: 'Courier New', monospace;
          font-size: 0.72rem;
          color: var(--accent-green);
          letter-spacing: 0.3px;
        }

        .metric-chip--purple {
          background: rgba(183,148,246,0.06);
          border-color: rgba(183,148,246,0.2);
        }

        .metric-chip--purple code {
          color: var(--accent-purple);
        }

        .metric-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--accent-green);
          flex-shrink: 0;
        }

        .metric-dot--purple {
          background: var(--accent-purple);
        }

        /* ── Card text ── */
        .sys-card-title {
          font-size: 1.1rem;
          font-weight: 700;
          line-height: 1.35;
          color: var(--text-primary);
        }

        .sys-card-desc {
          font-size: 0.875rem;
          color: var(--text-secondary);
          line-height: 1.65;
        }

        /* ── Architecture panel (hidden until hover) ── */
        .arch-panel {
          max-height: 0;
          overflow: hidden;
          opacity: 0;
          transition: max-height 0.35s ease, opacity 0.35s ease;
          border-left: 2px solid rgba(0,255,65,0.3);
          padding-left: 12px;
        }

        .sys-card:hover .arch-panel,
        .agent-card:hover .arch-panel {
          max-height: 240px;
          opacity: 1;
        }

        .arch-label {
          font-family: 'Courier New', monospace;
          font-size: 0.7rem;
          color: var(--accent-green);
          text-transform: uppercase;
          letter-spacing: 1.5px;
          margin-bottom: 6px;
        }

        .agent-card .arch-label {
          color: var(--accent-purple);
        }

        .agent-card .arch-panel {
          border-left-color: rgba(183,148,246,0.3);
        }

        .arch-text {
          font-size: 0.8rem;
          color: var(--text-secondary);
          line-height: 1.6;
        }

        /* ── Stack tags ── */
        .tag-row {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin-top: 4px;
        }

        .stack-tag {
          font-family: 'Courier New', monospace;
          font-size: 0.7rem;
          padding: 3px 8px;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 4px;
          color: var(--text-secondary);
        }

        .tag-row--sm { gap: 4px; }
        .stack-tag--sm { font-size: 0.65rem; }

        /* ── Card links ── */
        .card-links {
          display: flex;
          gap: 14px;
          margin-top: auto;
          padding-top: 8px;
        }

        .card-link {
          font-size: 0.8rem;
          font-weight: 600;
          color: var(--accent-green);
          transition: color 0.2s;
        }

        .card-link:hover { color: var(--accent-cyan); }

        .card-link--secondary { color: var(--accent-cyan); }
        .card-link--secondary:hover { color: var(--accent-green); }

        .card-link--muted {
          color: var(--text-dim);
          font-size: 0.78rem;
          font-weight: 400;
          cursor: default;
        }

        .card-link--sm { font-size: 0.75rem; }

        /* ── Other projects ── */
        .other-toggle-row {
          display: flex;
          justify-content: center;
        }

        .other-toggle-btn {
          padding: 10px 24px;
          background: transparent;
          border: 1px solid rgba(255,255,255,0.15);
          border-radius: 8px;
          color: var(--text-secondary);
          font-size: 0.85rem;
          cursor: pointer;
          transition: all 0.2s;
        }

        .other-toggle-btn:hover {
          border-color: var(--accent-green);
          color: var(--accent-green);
        }

        .other-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 20px;
          margin-top: 32px;
        }

        .other-card {
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 12px;
          overflow: hidden;
          transition: border-color 0.3s;
        }

        .other-card:hover {
          border-color: rgba(255,255,255,0.15);
        }

        .other-img-wrap {
          height: 130px;
          overflow: hidden;
        }

        .other-img-wrap img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          filter: grayscale(30%);
          transition: filter 0.3s;
        }

        .other-card:hover .other-img-wrap img {
          filter: grayscale(0%);
        }

        .other-body {
          padding: 16px 18px 18px;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .other-title {
          font-size: 0.95rem;
          font-weight: 700;
          color: var(--text-primary);
        }

        .other-desc {
          font-size: 0.8rem;
          color: var(--text-secondary);
          line-height: 1.55;
        }

        /* ── Responsive ── */
        @media (max-width: 768px) {
          .sys-grid {
            grid-template-columns: 1fr;
          }
          .other-grid {
            grid-template-columns: 1fr;
          }
          .proj-section-title {
            font-size: 1.7rem;
          }
        }
      `}</style>
    </section>
  );
};

export default Projects;
